/**
 * Small toggle button for enabling broadcast push notifications.
 * Drop into Account / Settings.
 */
import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth/AuthProvider";
import {
  currentPushPermission,
  disablePushNotifications,
  enablePushNotifications,
  isPushSupported,
} from "@/lib/push/usePushSubscription";

export const EnablePushButton = ({
  variant = "outline",
}: {
  variant?: "default" | "outline" | "ghost";
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [perm, setPerm] = useState<NotificationPermission | "unsupported">(
    "default",
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    isPushSupported().then((s) => {
      setSupported(s);
      setPerm(currentPushPermission());
    });
  }, []);

  if (supported === false) {
    return (
      <p className="text-xs text-muted-foreground">
        Push notifications aren't supported in this browser.
      </p>
    );
  }

  if (!user) return null;

  const enabled = perm === "granted";

  const handle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await disablePushNotifications(user.id);
        toast({ title: "Push notifications paused" });
      } else {
        const res = await enablePushNotifications(user.id);
        if (res.ok) {
          toast({ title: "Push notifications enabled" });
          setPerm("granted");
        } else {
          toast({
            title: "Couldn't enable push",
            description: "reason" in res ? res.reason : undefined,
            variant: "destructive",
          });
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      onClick={handle}
      disabled={busy}
      className="gap-2"
    >
      {enabled ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
      {enabled ? "Pause notifications" : "Enable notifications"}
    </Button>
  );
};
